import { DateTime } from 'luxon'
import {
  getTotalTaskOccurances,
  isEmpty,
  pluralize,
  sortTasksByPriorityDescending,
  type EntityId,
  type ProgramCompletion,
  type ProgramData,
  type ProgramTaskData,
  type ProgramUserData,
  type ProgramUserStatus,
} from '@terros/common'

export const PROGRAM_USER_GROUPS = ['current', 'upcoming', 'past'] as const
export type ProgramUserGroup = (typeof PROGRAM_USER_GROUPS)[number]

export type ProgramUserDataToGroup = Pick<ProgramUserData, 'programId' | 'status'>
export const groupProgramUsers = (users?: ProgramUserData[]): Map<ProgramUserGroup, ProgramUserData[]> => {
  const map = new Map<ProgramUserGroup, ProgramUserData[]>()
  PROGRAM_USER_GROUPS.forEach((group) => {
    map.set(group, [])
  })
  if (!users) return map
  for (const user of users ?? []) {
    const group = getProgramUserGroup(user)
    let existing = map.get(group)
    if (!existing) {
      existing = []
      map.set(group, existing)
    }
    existing.push(user)
  }
  return map
}

export const toReadableProgramUserGroup = (group: ProgramUserGroup, count?: number): string => {
  return `${getReadableProgramUserGroup(group)} ${pluralize(count ?? 0, 'User')}`
}

const getReadableProgramUserGroup = (group: ProgramUserGroup): string => {
  switch (group) {
    case 'current':
      return 'Current'
    case 'upcoming':
      return 'Upcoming'
    case 'past':
      return 'Past'
  }
}

export const getProgramUserGroup = (user: ProgramUserData): ProgramUserGroup => {
  switch (user.status) {
    case 'pending':
      return 'upcoming'
    case 'active':
      return 'current'
    case 'completed':
    case 'dropped':
      return 'past'
  }
}

export const stringifyUserTimeline = (user: ProgramUserData): string => {
  switch (user.status) {
    case 'active':
      return activeUserTimeline(user)
    case 'completed':
      return completedUserTimeline(user)
    case 'dropped':
      return droppedUserTimeline(user)
    case 'pending':
      return pendingUserTimeline(user)
  }
}

const activeUserTimeline = (user: ProgramUserData): string => {
  const startDate = DateTime.fromMillis(user.startDate).toLocaleString(DateTime.DATE_MED)
  return `Started ${startDate}`
}

const completedUserTimeline = (user: ProgramUserData): string => {
  const startDate = DateTime.fromMillis(user.startDate).toLocaleString(DateTime.DATE_MED)

  if (!user.completionDate) return startDate

  const endDate = DateTime.fromMillis(user.completionDate).toLocaleString(DateTime.DATE_MED)
  return `${startDate} - ${endDate}`
}

const droppedUserTimeline = (user: ProgramUserData): string => {
  const startDate = DateTime.fromMillis(user.startDate)
  const dropDate = user.droppedDate && DateTime.fromMillis(user.droppedDate).toLocaleString(DateTime.DATE_MED)
  return dropDate
    ? `${startDate.toLocaleString(DateTime.DATE_MED)} - ${dropDate}`
    : startDate.toLocaleString(DateTime.DATE_MED)
}

const pendingUserTimeline = (user: ProgramUserData): string => {
  const startDate = DateTime.fromMillis(user.startDate).toLocaleString(DateTime.DATE_MED)
  return `Starting ${startDate}`
}

export const getTaskWarning = (task: ProgramTaskData, program: Pick<ProgramData, 'totalDays'>): string | undefined => {
  if (task.schedule.type === 'none') return
  if (task.schedule.type === 'sequentialWeekday') return
  const totalOccurrences = getTotalTaskOccurances(task.schedule, program.totalDays)
  if (isEmpty(totalOccurrences)) return 'This task has no schedule'
  if (task.type !== 'WatchVideo') return
  const criteria = task.criteria
  if (isEmpty(criteria)) return 'This task has no videos'
  if (criteria.length < totalOccurrences) return 'This task has too few videos'
  if (criteria.length > totalOccurrences) return 'This task has too many videos'
}

export function sortProgramTasks(program: ProgramData): ProgramData {
  return {
    ...program,
    tasks: program.tasks.sort(sortTasksByPriorityDescending),
  }
}

export const toReadableProgramUserStatus = (status: ProgramUserStatus): string => {
  switch (status) {
    case 'active':
      return 'Active'
    case 'completed':
      return 'Completed'
    case 'dropped':
      return 'Dropped'
    case 'pending':
      return 'Pending'
  }
}

export const getProgramPermissionIds = (program: Pick<ProgramData, 'teamIds' | 'companyId'>): EntityId[] => {
  return [...(program.teamIds ?? []), program.companyId]
}

export const stringifyProgramCompletion = (completion: ProgramCompletion = 'timeline'): string => {
  switch (completion) {
    case 'taskCompletion':
      return 'Task Completion'
    case 'timeline':
      return 'Timeline'
    default:
      return 'Unknown'
  }
}
